import { DataProvider, fetchUtils } from "react-admin";

// Use VITE_API_URL for production (split architecture: Vercel → Railway)
// Empty string means same-origin (local development)
const apiUrl = import.meta.env.VITE_API_URL || "";

const httpClient = (url: string, options: fetchUtils.Options = {}) => {
  const customOptions: fetchUtils.Options = {
    ...options,
    credentials: "include", // Include cookies for session auth
  };
  return fetchUtils.fetchJson(url, customOptions);
};

/**
 * Helper to identify File objects from react-admin's FileInput
 */
const isFile = (candidate: unknown): candidate is { rawFile: File } => {
  return (
    candidate !== null &&
    typeof candidate === "object" &&
    "rawFile" in candidate &&
    (candidate as { rawFile: unknown }).rawFile instanceof File
  );
};

/**
 * Upload file directly to Cloudinary using signed upload
 * This keeps heavy files off your Express server - they go straight to Cloudinary CDN
 */
const uploadToCloudinary = async (file: File): Promise<{ publicId: string; resourceType: string }> => {
  // 1. Get signature from backend
  const signResponse = await httpClient(`${apiUrl}/api/cloudinary-sign`);
  const { signature, timestamp, cloudName, apiKey, folder } = signResponse.json;

  // 2. Determine resource type (image or video)
  const isVideo = file.type.startsWith("video/");
  const resourceType = isVideo ? "video" : "image";

  // 3. Upload directly to Cloudinary
  const formData = new FormData();
  formData.append("file", file);
  formData.append("api_key", apiKey);
  formData.append("timestamp", timestamp.toString());
  formData.append("signature", signature);
  formData.append("folder", folder);

  const uploadUrl = `https://api.cloudinary.com/v1_1/${cloudName}/${resourceType}/upload`;
  
  const response = await fetch(uploadUrl, {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || "Cloudinary upload failed");
  }

  const data = await response.json();
  
  // 4. Return the public_id (e.g., "hhsbuild-cms/abc123")
  return {
    publicId: data.public_id,
    resourceType: data.resource_type,
  };
};

/**
 * Custom data provider for react-admin that works with our Express API
 * Supports: getList, getOne, getMany, create, update, delete, deleteMany
 * 
 * For media uploads, files are sent directly to Cloudinary (not through Express)
 * to optimize bandwidth and server performance.
 */
export const dataProvider: DataProvider = {
  getList: async (resource, params) => {
    const { page, perPage } = params.pagination || { page: 1, perPage: 10 };
    const { field, order } = params.sort || { field: "createdAt", order: "DESC" };
    const query = {
      _start: ((page - 1) * perPage).toString(),
      _end: (page * perPage).toString(),
      _sort: field,
      _order: order,
      ...params.filter,
    };

    const queryString = Object.entries(query)
      .filter(([, value]) => value !== undefined && value !== "")
      .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
      .join("&");

    const url = `${apiUrl}/api/${resource}?${queryString}`;
    const { json, headers } = await httpClient(url);

    // Parse Content-Range header: "media 0-9/100"
    const contentRange = headers.get("Content-Range");
    let total = json.length;
    if (contentRange) {
      const match = contentRange.match(/\/(\d+)$/);
      if (match) {
        total = parseInt(match[1], 10);
      }
    }

    return {
      data: json,
      total,
    };
  },

  getOne: async (resource, params) => {
    const url = `${apiUrl}/api/${resource}/${params.id}`;
    const { json } = await httpClient(url);
    return { data: json };
  },

  getMany: async (resource, params) => {
    const results = await Promise.all(
      params.ids.map((id) =>
        httpClient(`${apiUrl}/api/${resource}/${id}`).then(({ json }) => json)
      )
    );
    return { data: results };
  },

  getManyReference: async (resource, params) => {
    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;
    const query = {
      _start: ((page - 1) * perPage).toString(),
      _end: (page * perPage).toString(),
      _sort: field,
      _order: order,
      [params.target]: params.id,
      ...params.filter,
    };

    const queryString = Object.entries(query)
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
      .join("&");

    const url = `${apiUrl}/api/${resource}?${queryString}`;
    const { json, headers } = await httpClient(url);

    const contentRange = headers.get("Content-Range");
    let total = json.length;
    if (contentRange) {
      const match = contentRange.match(/\/(\d+)$/);
      if (match) {
        total = parseInt(match[1], 10);
      }
    }

    return {
      data: json,
      total,
    };
  },

  create: async (resource, params) => {
    // For media, handle direct Cloudinary upload
    if (resource === "media" && params.data.file && isFile(params.data.file)) {
      // 1. Upload to Cloudinary directly (bypasses Express server)
      const { publicId, resourceType } = await uploadToCloudinary(params.data.file.rawFile);

      // 2. Send metadata + publicId to our API to create database record
      const { json } = await httpClient(`${apiUrl}/api/${resource}`, {
        method: "POST",
        body: JSON.stringify({
          publicId,
          resourceType,
          title: params.data.title,
          description: params.data.description,
          altText: params.data.altText,
          category: params.data.category,
          tags: params.data.tags,
          featured: params.data.featured,
        }),
      });

      return { data: json };
    }

    // Regular JSON create for non-media resources
    const { json } = await httpClient(`${apiUrl}/api/${resource}`, {
      method: "POST",
      body: JSON.stringify(params.data),
    });
    return { data: json };
  },

  update: async (resource, params) => {
    // For media, only send allowed update fields (not the entire record)
    if (resource === "media") {
      // Helper to filter out null/undefined values (Zod rejects null for optional string fields)
      const cleanData = (data: Record<string, unknown>) => {
        const cleaned: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(data)) {
          if (value !== null && value !== undefined) {
            cleaned[key] = value;
          }
        }
        return cleaned;
      };

      // Check if there's a new file to upload
      if (params.data.file && isFile(params.data.file)) {
        // Upload new file to Cloudinary
        const { publicId, resourceType } = await uploadToCloudinary(params.data.file.rawFile);

        // Update record with new publicId and allowed metadata fields only
        const { json } = await httpClient(`${apiUrl}/api/${resource}/${params.id}`, {
          method: "PUT",
          body: JSON.stringify(cleanData({
            publicId,
            resourceType,
            title: params.data.title,
            description: params.data.description,
            altText: params.data.altText,
            category: params.data.category,
            tags: params.data.tags,
            featured: params.data.featured,
            sortOrder: params.data.sortOrder,
          })),
        });
        return { data: json };
      }

      // Regular metadata update - only send allowed fields
      // The updateMediaSchema only accepts: title, description, altText, category, tags, featured, sortOrder
      const { json } = await httpClient(`${apiUrl}/api/${resource}/${params.id}`, {
        method: "PUT",
        body: JSON.stringify(cleanData({
          title: params.data.title,
          description: params.data.description,
          altText: params.data.altText,
          category: params.data.category,
          tags: params.data.tags,
          featured: params.data.featured,
          sortOrder: params.data.sortOrder,
        })),
      });
      return { data: json };
    }

    // Regular update for non-media resources
    const { json } = await httpClient(`${apiUrl}/api/${resource}/${params.id}`, {
      method: "PUT",
      body: JSON.stringify(params.data),
    });
    return { data: json };
  },

  updateMany: async (resource, params) => {
    const results = await Promise.all(
      params.ids.map((id) =>
        httpClient(`${apiUrl}/api/${resource}/${id}`, {
          method: "PUT",
          body: JSON.stringify(params.data),
        }).then(({ json }) => json.id)
      )
    );
    return { data: results };
  },

  delete: async (resource, params) => {
    const { json } = await httpClient(`${apiUrl}/api/${resource}/${params.id}`, {
      method: "DELETE",
    });
    return { data: json };
  },

  deleteMany: async (resource, params) => {
    // Use batch delete endpoint if available
    if (resource === "media") {
      const response = await fetch(`${apiUrl}/api/${resource}/batch-delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: params.ids }),
        credentials: "include",
      });
      
      if (!response.ok) {
        throw new Error("Batch delete failed");
      }
      
      return { data: params.ids };
    }

    // Fallback: delete one by one
    await Promise.all(
      params.ids.map((id) =>
        httpClient(`${apiUrl}/api/${resource}/${id}`, {
          method: "DELETE",
        })
      )
    );
    return { data: params.ids };
  },
};

export default dataProvider;
